// src/pages/admin/HeroManagement.tsx
import { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Image as ImageIcon, ChevronUp, ChevronDown, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/AdminLayout';
import imageCompression from 'browser-image-compression';

interface HeroSection {
  id: string;
  title: string;
  subtitle: string;
  image_url: string;
  link_url: string;
  cta_text: string;
  badge?: string;
  display_order: number;
  is_active: boolean;
  // We'll store dimensions as a string in the badge field or as metadata
  image_dimensions?: string;
}

export function HeroManagement() {
  const [heroSections, setHeroSections] = useState<HeroSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHero, setEditingHero] = useState<HeroSection | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    link_url: '',
    cta_text: '',
    badge: '',
    display_order: 0,
    is_active: true,
    image_width: 1920,
    image_height: 1080
  });

  // Suggested hero dimensions
const suggestedSizes = [
  { name: 'Full HD (Standard)', width: 1920, height: 1080 },
  { name: 'Ultra Large', width: 2560, height: 1080 },
  { name: 'Portrait', width: 1080, height: 1920 },
  { name: 'Carré', width: 1080, height: 1080 },
  { name: 'Optimisé Mobile', width: 1080, height: 1350 },
];

  useEffect(() => {
    fetchHeroSections();
  }, []);

  const fetchHeroSections = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('hero_sections')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setHeroSections(data || []);
    } catch (err) {
      console.error('Error fetching hero sections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);

      if (!formData.title || !formData.subtitle || !formData.link_url || !formData.cta_text) {
        setError('Veuillez remplir tous les champs obligatoires');
        setSubmitting(false);
        return;
      }

      // Store dimensions as a string in the badge field or create a metadata field
      const dimensionsString = `${formData.image_width}x${formData.image_height}`;
      
      // Create the data object without the image_width and image_height fields
      const dataToSubmit = {
        title: formData.title,
        subtitle: formData.subtitle,
        image_url: formData.image_url,
        link_url: formData.link_url,
        cta_text: formData.cta_text,
        badge: formData.badge ? `${formData.badge}|${dimensionsString}` : dimensionsString,
        display_order: formData.display_order,
        is_active: formData.is_active
      };

      if (editingHero) {
        const { data, error } = await supabase
          .from('hero_sections')
          .update(dataToSubmit)
          .eq('id', editingHero.id)
          .select();

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('hero_sections')
          .insert([dataToSubmit])
          .select();

        if (error) throw error;
      }

      await fetchHeroSections();
      closeModal();
      alert(editingHero ? 'Section Hero mise à jour avec succès' : 'Section Hero créée avec succès');
    } catch (error) {
      console.error('Error saving hero section:', error);
      setError('Erreur lors de l\'enregistrement de la section Hero');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette section Hero ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('hero_sections')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchHeroSections();
      alert('Section Hero supprimée avec succès');
    } catch (error) {
      console.error('Error deleting hero section:', error);
      alert('Erreur lors de la suppression de la section Hero');
    }
  };

  const toggleActive = async (hero: HeroSection) => {
    try {
      const { error } = await supabase
        .from('hero_sections')
        .update({ is_active: !hero.is_active })
        .eq('id', hero.id);

      if (error) throw error;
      await fetchHeroSections();
    } catch (error) {
      console.error('Error toggling hero status:', error);
      alert('Erreur lors de la mise à jour du statut Hero');
    }
  };

  const openModal = (hero?: HeroSection) => {
    if (hero) {
      setEditingHero(hero);
      
      // Extract dimensions from badge if available
      let width = 1920;
      let height = 1080;
      let badge = hero.badge || '';
      
      if (hero.badge && hero.badge.includes('|')) {
        const parts = hero.badge.split('|');
        badge = parts[0];
        const dimensions = parts[1];
        if (dimensions && dimensions.includes('x')) {
          const [w, h] = dimensions.split('x');
          width = parseInt(w, 10) || 1920;
          height = parseInt(h, 10) || 1080;
        }
      } else if (hero.badge && hero.badge.includes('x')) {
        const [w, h] = hero.badge.split('x');
        width = parseInt(w, 10) || 1920;
        height = parseInt(h, 10) || 1080;
        badge = '';
      }
      
      setFormData({
        title: hero.title,
        subtitle: hero.subtitle,
        image_url: hero.image_url || '',
        link_url: hero.link_url,
        cta_text: hero.cta_text,
        badge: badge,
        display_order: hero.display_order,
        is_active: hero.is_active,
        image_width: width,
        image_height: height
      });
    } else {
      setEditingHero(null);
      setFormData({
        title: '',
        subtitle: '',
        image_url: '',
        link_url: '',
        cta_text: '',
        badge: '',
        display_order: heroSections.length,
        is_active: true,
        image_width: 1920,
        image_height: 1080
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHero(null);
    setError(null);
  };

  const handleImageUpload = (url: string) => {
    setFormData(prev => ({ ...prev, image_url: url }));
  };

  const handleImageRemove = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    
    const newSections = [...heroSections];
    const temp = newSections[index];
    newSections[index] = newSections[index - 1];
    newSections[index - 1] = temp;
    
    newSections[index].display_order = index;
    newSections[index - 1].display_order = index - 1;
    
    setHeroSections(newSections);
    
    try {
      await supabase
        .from('hero_sections')
        .update({ display_order: index })
        .eq('id', newSections[index].id);
        
      await supabase
        .from('hero_sections')
        .update({ display_order: index - 1 })
        .eq('id', newSections[index - 1].id);
    } catch (err) {
      console.error('Error reordering hero sections:', err);
      fetchHeroSections();
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === heroSections.length - 1) return;
    
    const newSections = [...heroSections];
    const temp = newSections[index];
    newSections[index] = newSections[index + 1];
    newSections[index + 1] = temp;
    
    newSections[index].display_order = index;
    newSections[index + 1].display_order = index + 1;
    
    setHeroSections(newSections);
    
    try {
      await supabase
        .from('hero_sections')
        .update({ display_order: index })
        .eq('id', newSections[index].id);
        
      await supabase
        .from('hero_sections')
        .update({ display_order: index + 1 })
        .eq('id', newSections[index + 1].id);
    } catch (err) {
      console.error('Error reordering hero sections:', err);
      fetchHeroSections();
    }
  };

  // Helper function to extract dimensions from badge
  const getDimensionsFromBadge = (badge?: string) => {
    if (!badge) return { width: 1920, height: 1080 };
    
    if (badge.includes('|')) {
      const parts = badge.split('|');
      const dimensions = parts[1];
      if (dimensions && dimensions.includes('x')) {
        const [w, h] = dimensions.split('x');
        return {
          width: parseInt(w, 10) || 1920,
          height: parseInt(h, 10) || 1080
        };
      }
    } else if (badge.includes('x')) {
      const [w, h] = badge.split('x');
      return {
        width: parseInt(w, 10) || 1920,
        height: parseInt(h, 10) || 1080
      };
    }
    
    return { width: 1920, height: 1080 };
  };

  // Helper function to get just the badge text (without dimensions)
  const getBadgeText = (badge?: string) => {
    if (!badge) return '';
    
    if (badge.includes('|')) {
      return badge.split('|')[0];
    }
    
    // If it's just dimensions, return empty string
    if (badge.includes('x')) {
      return '';
    }
    
    return badge;
  };

  // Simple file input component
  const SimpleFileInput = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);
      try {
        const options = {
          maxSizeMB: 1.5,
          maxWidthOrHeight: Math.max(formData.image_width, formData.image_height),
          useWebWorker: true,
          fileType: 'image/webp',
        };

        const compressedFile = await imageCompression(file, options);
        const fileExt = compressedFile.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('images')
          .upload(filePath, compressedFile, {
            cacheControl: '31536000',
            upsert: false,
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('images')
          .getPublicUrl(filePath);

        handleImageUpload(publicUrl);
      } catch (err) {
        console.error('Error uploading image:', err);
        setError('Erreur lors du téléchargement de l\'image');
      } finally {
        setUploading(false);
      }
    };

    return (
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={uploading}
          className="hidden"
        />
        {formData.image_url ? (
          <div className="space-y-3">
            <div className="relative w-full border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-100" 
                 style={{ paddingBottom: `${(formData.image_height / formData.image_width) * 100}%` }}>
              <img
                src={formData.image_url}
                alt="Aperçu"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={handleImageRemove}
                className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-colors"
                title="Supprimer l'image"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="text-xs text-gray-500 text-center">
              Dimensions: {formData.image_width} x {formData.image_height}px
            </div>
          </div>
        ) : (
          <div 
            className="w-full h-64 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#D4452C] hover:bg-gray-50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4452C] mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Téléchargement en cours...</p>
              </div>
            ) : (
              <div className="text-center">
                <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Cliquez pour télécharger une image</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP, GIF (MAX. 10MB)</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout currentPage="hero">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion de la section Hero</h1>
            <p className="text-gray-600 text-sm mt-1">
              Gérer les sections Hero affichées sur la page d'accueil
            </p>
          </div>
          <button
            onClick={() => openModal()}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all"
          >
            <Plus className="h-4 w-4" />
            <span>Nouvelle section Hero</span>
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Hero Sections List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4452C] mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des sections Hero...</p>
            </div>
          </div>
        ) : heroSections.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune section Hero</h3>
            <p className="text-gray-600 mb-6">
              Commencez par ajouter votre première section Hero
            </p>
            <button
              onClick={() => openModal()}
              className="inline-flex items-center space-x-2 bg-[#D4452C] text-white px-4 py-2 rounded-lg hover:bg-[#b83a24] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter une section Hero</span>
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dimensions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Titre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sous-titre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lien
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CTA
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Badge
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {heroSections.map((hero, index) => {
                    const dimensions = getDimensionsFromBadge(hero.badge);
                    const badgeText = getBadgeText(hero.badge);
                    
                    return (
                      <tr key={hero.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-20 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                            {hero.image_url ? (
                              <img
                                src={hero.image_url}
                                alt={hero.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-gray-500 text-xl font-bold">
                                {hero.title.charAt(0)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {dimensions.width} x {dimensions.height}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{hero.title}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {hero.subtitle}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {hero.link_url}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {hero.cta_text}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {badgeText ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {badgeText}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            hero.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {hero.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                              className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                              title="Monter"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleMoveDown(index)}
                              disabled={index === heroSections.length - 1}
                              className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                              title="Descendre"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => toggleActive(hero)}
                              className={`p-2 rounded-lg transition-colors ${
                                hero.is_active
                                  ? 'text-green-600 hover:bg-green-50'
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                              title={hero.is_active ? 'Désactiver' : 'Activer'}
                            >
                              {hero.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => openModal(hero)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(hero.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingHero ? 'Modifier la section Hero' : 'Nouvelle section Hero'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                    {error}
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    required
                    placeholder="Entrez le titre du Hero"
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sous-titre *
                  </label>
                  <textarea
                    value={formData.subtitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    rows={3}
                    required
                    placeholder="Entrez le sous-titre du Hero"
                  />
                </div>

                {/* Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Hero *
                  </label>
                  <SimpleFileInput />
                </div>

                {/* Image Dimensions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dimensions de l'image
                  </label>
                  
                  {/* Suggested Sizes */}
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">Tailles suggérées:</p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedSizes.map((size) => (
                        <button
                          key={size.name}
                          type="button"
                          onClick={() => setFormData(prev => ({ 
                            ...prev, 
                            image_width: size.width, 
                            image_height: size.height 
                          }))}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                            formData.image_width === size.width && formData.image_height === size.height
                              ? 'bg-[#D4452C] text-white border-[#D4452C]'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-[#D4452C]'
                          }`}
                        >
                          {size.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Dimensions */}
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={formData.image_width}
                        onChange={(e) => setFormData(prev => ({ ...prev, image_width: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                        placeholder="Largeur"
                        min="100"
                        max="4000"
                      />
                    </div>
                    <span className="text-gray-500">×</span>
                    <div className="flex-1">
                      <input
                        type="number"
                        value={formData.image_height}
                        onChange={(e) => setFormData(prev => ({ ...prev, image_height: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                        placeholder="Hauteur"
                        min="100"
                        max="4000"
                      />
                    </div>
                    <span className="text-sm text-gray-500">px</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Ratio: {(formData.image_width / formData.image_height).toFixed(2)}:1
                  </p>
                </div>

                {/* Link URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL du lien *
                  </label>
                  <input
                    type="text"
                    value={formData.link_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    required
                    placeholder="/listings ou https://example.com"
                  />
                </div>

                {/* CTA Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Texte CTA *
                  </label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    required
                    placeholder="Explorer maintenant"
                  />
                </div>

                {/* Badge */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Badge (Optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.badge}
                    onChange={(e) => setFormData(prev => ({ ...prev, badge: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    placeholder="Premium, Nouveau, etc."
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-[#D4452C] border-gray-300 rounded focus:ring-[#D4452C]"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Actif
                  </label>
                </div>

                {/* Actions */}
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white py-2 rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Enregistrement...' : (editingHero ? 'Mettre à jour' : 'Créer')}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}